import { useEffect } from 'react'
import { createFileRoute, Outlet, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { authReady, useAuthStore } from '@/stores/auth.store'
import { loginSearchFor } from '@/features/auth/signup/project-login-target'

/**
 * Pathless auth-guard layout for the Account-Management v2 console.
 *
 * Distinct from `_app.tsx`: the account console renders its OWN chrome (top
 * chrome + settings rail), not the three-panel agent `Shell`. This layout only
 * enforces authentication and renders an `<Outlet/>`; the workspace-scoped
 * chrome lives in `/w/$workspaceId`. In the `acc-mocked` dev mode the dev-auth
 * bypass makes `authReady` resolve immediately with a seeded dev user, so the
 * console boots straight in.
 */
export const Route = createFileRoute('/_ws')({
  beforeLoad: async ({ location }) => {
    await authReady
    if (!useAuthStore.getState().user) {
      // Preserve the pre-login destination for the SignupForm to consume.
      throw redirect({
        to: '/login',
        search: loginSearchFor(location.pathname, location.searchStr ?? ''),
      })
    }
  },
  component: WsLayout,
})

function WsLayout() {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const router = useRouter()
  useEffect(() => {
    if (user) return
    // A settled sign-out (the auth store ignores another tab's momentary null)
    // keeps where the user was, so signing back in returns them there.
    const { pathname, searchStr } = router.state.location
    void navigate({ to: '/login', search: loginSearchFor(pathname, searchStr), replace: true })
  }, [user, navigate, router])
  if (!user) return null
  return <Outlet />
}
